<script setup lang="ts">
import { from } from '@nuts/auto-delivery-sdk/dist/utils/money';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { browseProducts } from '@/api/algolia';
import { getBasePrice } from '@/api/customizers/boxes';
import ToastNotification from '@/components/base/ToastNotification.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import BaseCustomizer from '@/components/customizer/BaseCustomizer.vue';
import BuyBlock from '@/components/customizer/box/BuyBlock.vue';
import { BoxCustomizer } from '@/lib/customizers/boxes';
import { SkuIngredient } from '@/lib/customizers/ingredients';
import { isDefined } from '@/utils/isDefined';

const route = useRoute();
const router = useRouter();

const boxCustomizer = reactive(new BoxCustomizer());

const ingredients = ref<SkuIngredient[]>([]);
const priceBeforeDiscount = computed(() => boxCustomizer.calculatePriceBeforeDiscount());
const totalPrice = computed(() => boxCustomizer.calculateTotal());
const hashedCode = computed(() => boxCustomizer.encodeCustomizerSelections());

const notificationMessage = ref<string>();

const loadBasePrice = async () => {
  const price = await getBasePrice();
  boxCustomizer.basePrice = price ?? from(0);
};

const loadIngredients = async () => {
  const products = await browseProducts({
    query: 'mini box',
    restrictSearchableAttributes: ['shortVariantName'],
    typoTolerance: false,
  });

  ingredients.value = products
    .filter((product) => !product.outOfStock)
    .map((product) => {
      try {
        const ingredient = SkuIngredient.fromAlgoliaVariant(product);
        return ingredient;
      } catch (e) {
        return undefined;
      }
    })
    .filter(isDefined);
};

onMounted(async () => {
  await Promise.all([loadBasePrice(), loadIngredients()]);

  boxCustomizer.prefillSelections(ingredients.value, route.query.code as string);
});

const setNotification = (message: string) => {
  notificationMessage.value = message;
};

const handleAddIngredient = (productKey: string) => {
  const slot = boxCustomizer.selections.findIndex((cell) => !cell);
  const addedIngredient = ingredients.value.find((ing) => ing.productKey === productKey);

  if (!addedIngredient) return;

  if (boxCustomizer.selections.filter(isDefined).length === boxCustomizer.ingredientLimits.max) {
    setNotification(`You've reached the max of ${boxCustomizer.ingredientLimits.max} ingredients`);
    return;
  }

  boxCustomizer.setSelection(slot, addedIngredient);
};

const handleRemoveIngredient = (productKey: string) => {
  const slot = boxCustomizer.selections.findLastIndex((cell) => cell?.productKey === productKey);
  const ingredient = boxCustomizer.selections[slot];

  if (slot > -1) {
    setNotification(`${ingredient!.name} has been removed from your box`);
    boxCustomizer.setSelection(slot);
  }
};

watch(hashedCode, () => {
  if (route.query.code !== hashedCode.value) {
    router.replace({ query: { ...route.query, code: hashedCode.value } });
  }
});
</script>

<template>
  <BaseCustomizer
    v-slot="{ digitalGifting, handleOpenIngredientsModal }"
    categoryLevel="lvl0"
    :ingredients
    :maxIngredients="boxCustomizer.ingredientLimits.max"
    mode="box"
    :selections="boxCustomizer.selections"
    @add-ingredient="handleAddIngredient"
    @remove-ingredient="handleRemoveIngredient"
  >
    <BuyBlock
      v-model:quantity="boxCustomizer.quantity"
      :boxContent="boxCustomizer.selections"
      :digitalGiftingProduct="digitalGifting"
      :priceBeforeDiscount
      :totalPrice
      @open-modal="handleOpenIngredientsModal"
      @remove="handleRemoveIngredient"
      data-test="custom-box-buy-block"
    />
  </BaseCustomizer>
  <ToastNotification :show="!!notificationMessage" @hide="notificationMessage = ''">
    <BaseBodyText>{{ notificationMessage }}</BaseBodyText>
  </ToastNotification>
</template>

<style lang="scss" scoped>
.custom-min-h {
  min-height: calc(100vh - 80px); // screen height - nav height
}

.hidden {
  visibility: visible;
}
</style>
